import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import BottomFixedContainer from '@/components/molecule/BottomFixedContainer';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import { BUTTON_CONST } from '@/src/constants/buttons';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import DetailSection from '@/components/organism/DetailSection';
import { DetailSctionRenderer } from '@/components/organism/DetailSection/renderer';
import { NAV_CONST } from '@/src/constants/nav';
import Table from '@/components/atom/Table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import SectionCard from '@/components/molecule/SectionCard';
import {
  getRecyclingRequestsByIdAPI,
  putRecyclingRequestAPI,
} from '@/src/apis/rest/recyclingRequests';
import { formatter } from '@/src/utils/formatter';
import {
  recyclingRequestDeliveryColumn,
  recyclingRequestProductOptionColumn,
} from '@/src/pages/RecyclingRequests/column';
import RequestCancelModal from '@/src/pages/RecyclingRequests/components/RequestCancelModal';
import PATH from '@/src/routes/path';
import { useToast } from '@/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import MultipleImagePreview from '@/src/components/molecule/MultipleImagePreview';
import AlertDialog from '@/src/components/atom/AlertDialog';

type ModalState = 'NONE' | 'CANCEL';

const BACK_PATH = PATH.RECYCLING_REQUESTS_LIST;

function RecyclingRequestsDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [modalState, setModalState] = useState<ModalState>('NONE');
  const { toast } = useToast();

  const productGridRef = useRef<AgGridReact>(null);
  const deliveryGridRef = useRef<AgGridReact>(null);

  const gramToKiloGram = (gram: number): number => {
    const kiloGram = gram / 1000;
    const parsedKiloGram = parseFloat(kiloGram.toFixed(2));
    return isNaN(parsedKiloGram) ? 0 : parsedKiloGram;
  };

  const handleCopyClick = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        toast({
          variant: 'success',
          title: TOAST_CONST.saveSuccess.title,
          description: '클립보드에 복사되었습니다',
        });
      } catch (e) {
        toast({
          variant: 'error',
          title: TOAST_CONST.error.title,
          description: '클립보드 복사에 실패했습니다',
        });
      }
    },
    [toast],
  );

  const {
    data: requestData,
    isFetching: isRefreshFetching,
    refetch: refreshRecyclingRequest,
  } = useQuery({
    queryKey: ['recycling-product', id],
    queryFn: () => getRecyclingRequestsByIdAPI(Number(id)),
    enabled: !!id,
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  useEffect(() => {
    if (isRefreshFetching && deliveryGridRef.current) {
      deliveryGridRef.current.api?.showLoadingOverlay();
    }
  }, [isRefreshFetching]);

  const putRequestCancel = useMutation({
    mutationKey: ['customers', 'normal', id],
    mutationFn: ({ id }: { id: number }) => putRecyclingRequestAPI(id),
  });

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(-1)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };

  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  const handleCancelRequest = () => {
    putRequestCancel.mutate(
      {
        id: Number(id),
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: '성공',
            description: '수거 신청 상태가 변경되었습니다.',
          });
          refreshRecyclingRequest();
        },
        onError: e => {
          toast({
            variant: 'error',
            title: '실패',
            description: e.message,
          });
        },
      },
    );
  };

  if (!requestData) return null;

  return (
    <>
      <RequestCancelModal
        requestData={requestData}
        customer={requestData?.customer}
        setOpen={setIsCancelModalOpen}
        isOpen={isCancelModalOpen}
        submitCallback={() => navigate(PATH.RECYCLING_REQUESTS_LIST)}
      />
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.recyclingRequests,
          NAV_CONST.recyclingRequestsDetail,
        ]}
        pageTitle={NAV_CONST.recyclingRequestsDetail}
      />
      <DetailSection
        sectionTitle={
          <div className="flex w-full items-center justify-between">
            <span className="font-pretendardSemiBold text-b18sb">
              수거 신청 회원 정보
            </span>
          </div>
        }
        data={requestData}
        contents={[
          [
            {
              key: 'customerType',
              lable: '회원 유형',
              valueSetter: data =>
                data?.customer?.type
                  ? data?.customer?.type === 'normal'
                    ? '일반 회원'
                    : '사업자 회원'
                  : '-',
            },
            {
              key: 'nick',
              lable: '닉네임',
              valueSetter: data => data?.customer?.nick || '-',
            },
            {
              key: 'name',
              lable: '이름',
              valueSetter: data => data?.customer?.name || '-',
            },
          ],
          [
            {
              key: 'phone',
              lable: '핸드폰 번호',
              valueSetter: data =>
                data?.customer?.phone
                  ? formatter.PHONE(data.customer?.phone)
                  : '-',
            },
            {
              key: 'customerAddress',
              lable: '기본 주소',
              valueSetter: data => {
                const address =
                  data?.customer?.customerAddresses?.[0]?.street || '-';
                return address || '-';
              },
            },
          ],
        ]}
      />
      <DetailSection
        className="mt-[24px]"
        sectionTitle={
          <div className="mt-[8] flex w-full items-center justify-between">
            <span className="font-pretendardSemiBold text-b18sb">
              수거 신청 정보
            </span>
          </div>
        }
        data={requestData}
        contents={[
          [
            {
              key: 'createdAt',
              lable: '신청 일시',
              render: DetailSctionRenderer.DATE({
                format: 'YYYY-MM-DD HH:mm:ss',
              }),
            },
            {
              key: 'reqNo',
              lable: '수거 번호',
            },
            {
              key: 'status',
              lable: '진행 상태',
              render:
                requestData.status === 'PROCESSING' &&
                requestData.isAvailablePartialComplete
                  ? DetailSctionRenderer.BADGE({
                      options: [
                        {
                          label: '부분 수거중',
                          key: 'PROCESSING',
                          badgeColor: 'lightPurple',
                        },
                      ],
                    })
                  : DetailSctionRenderer.BADGE({
                      options: [
                        {
                          label: '수거 신청',
                          key: 'REQUEST',
                          badgeColor: 'lightYellow',
                        },
                        {
                          label: '수거중',
                          key: 'PROCESSING',
                          badgeColor: 'lightGreen',
                        },
                        {
                          label: '수거완료',
                          key: 'SHIPPING',
                          badgeColor: 'lightBlue',
                        },
                        {
                          label: '수거취소 - 택배사 취소',
                          key: 'CANCELED_BY_CARRIER',
                          badgeColor: 'lightRed',
                        },
                        {
                          label: '수거취소 - 고객 취소',
                          key: 'CANCELED_BY_CUSTOMER',
                          badgeColor: 'lightRed',
                        },
                        {
                          label: '수거취소 - 관리자 취소',
                          key: 'CANCELED_BY_ADMIN',
                          badgeColor: 'lightRed',
                        },
                        {
                          label: '배달완료',
                          key: 'COMPLETED',
                          badgeColor: 'lightGray',
                        },
                        {
                          label: '부분 수거완료',
                          key: 'PARTIAL_COMPLETE',
                          badgeColor: 'lightBlue',
                        },
                        {
                          label: '미배달',
                          key: 'SHIPPING_DELAYED',
                          badgeColor: 'lightRed',
                        },
                        {
                          label: '미집하',
                          key: 'PROCESS_DELAYED',
                          badgeColor: 'lightRed',
                        },
                      ],
                    }),
            },
            {
              key: 'cancelReason',
              lable: '취소 사유',
              isHidden: ![
                'CANCELED_BY_CARRIER',
                'CANCELED_BY_CUSTOMER',
                'CANCELED_BY_ADMIN',
              ].includes(requestData?.status || ''),
              valueSetter: data => {
                const status = data?.status;
                if (status === 'CANCELED_BY_CUSTOMER') return '고객 취소';
                if (status === 'CANCELED_BY_CARRIER') return '택배사 취소';
                if (status === 'CANCELED_BY_ADMIN')
                  return data?.cancelReason || '-';
                return '-';
              },
            },
            {
              key: 'totalCarbonEmissionReduction',
              lable: '탄소 감축량 (kg)',
              valueSetter: data =>
                `${gramToKiloGram(Number(data?.totalCarbonEmissionReduction))}` ||
                '-',
            },
            {
              key: 'totalPineScore',
              lable: '소나무 기여도',
            },
            {
              key: 'totalWeight',
              lable: '신청 무게 (kg)',
              valueSetter: data =>
                `${gramToKiloGram(Number(data?.totalWeight))}` || '-',
            },
            {
              key: 'boxCount',
              lable: '박스 수',
            },
          ],
          [
            {
              key: 'requestAddressName',
              lable: '주소명',
              valueSetter: data => `${data?.requestAddressName || '-'}`,
            },
            {
              key: 'requestName',
              lable: '보내는 사람',
            },
            {
              key: 'requestPhone',
              lable: '핸드폰 번호',
            },
            {
              key: 'requestAddress',
              lable: '주소',
            },
            {
              key: 'requestMemo',
              lable: '수거 요청사항',
            },
            {
              key: 'recyclingRequestMedia',
              lable: '수거 사진',
              valueSetter: data => {
                return JSON.stringify(data?.recyclingRequestMedias);
              },
              render: value => {
                const result = JSON.parse(value || '[]');

                if (Array.isArray(result) && result.length < 1) return '-';

                return (
                  <MultipleImagePreview
                    images={result}
                    width={128}
                    height={128}
                    isPreviewRatioFixed={false}
                  />
                );
              },
            },
          ],
        ]}
      />
      <div className="mt-[24px]">
        <SectionCard
          cardTitle="수거 배송 정보"
          contentProps={{
            className: 'flex flex-col gap-y-4',
          }}
          rightAccessary={
            <Button
              buttonSize="small"
              buttonColor="outline"
              onClick={() => handleOpenModal('CANCEL')}
              disabled={
                requestData?.status === 'COMPLETED' ||
                !requestData?.isAvailablePartialComplete
              }
              className="disabled:border disabled:border-gray4"
            >
              수거 정보 완료 처리하기
            </Button>
          }
        >
          <Table
            tableClassName="mt-4"
            ref={deliveryGridRef}
            isPagination={false}
            isTotal={true}
            rowData={requestData?.recyclingRequestDetails || []}
            total={requestData?.recyclingRequestDetails?.length || 0}
            columnDefs={recyclingRequestDeliveryColumn({
              onCopyClick: handleCopyClick,
              logisProvider: requestData?.logisProvider,
              isAvailablePartialComplete:
                requestData?.isAvailablePartialComplete,
            })}
          />
        </SectionCard>
      </div>
      <div className="mb-[100px] mt-[24px]">
        <SectionCard
          cardTitle="품목 정보"
          contentProps={{
            className: 'flex flex-col gap-y-4',
          }}
        >
          <Table
            tableClassName="mt-4"
            ref={productGridRef}
            rowData={requestData?.recyclingRequestProducts || []}
            columnDefs={recyclingRequestProductOptionColumn()}
            isPagination={false}
          />
        </SectionCard>
      </div>
      {renderBottomFixedContent()}

      {modalState === 'CANCEL' && (
        <AlertDialog
          open={modalState === 'CANCEL'}
          onOpenChange={() => handleToggleModal('CANCEL')}
          okText="확인"
          title="수거 신청 상태를 완료 처리하시겠습니까?"
          okButtonProps={{
            isLoading: putRequestCancel.isPending,
          }}
          onOk={handleCancelRequest}
        >
          <p className="whitespace-pre-wrap">{`미집하/미배달 상태 건에 대해 임의취소로 변경하게 됩니다.\n상태 변경 이후에 취소를 할 수 없습니다. 정말 완료 처리하시겠습니까?`}</p>
        </AlertDialog>
      )}
    </>
  );
}

export default RecyclingRequestsDetailPage;
